import { dispatch } from './store';
import { MILLISECONDS_MULTIPLIER, DURATION_PADDING } from './constants';
import { ToastItem, ToastType } from './types';

/**
 * Executes callback, deferred to the next loop iteration.
 *
 * @param callback
 */
const nextTick = (callback: () => void) => {
  window.setTimeout(callback, 0);
};

/**
 * Extracts the transition duration from element,
 * which is then converted to milliseconds.
 *
 * @param element
 */
const getTransitionDuration = (element: Element): number => {
  if (!element) return 0;

  const { transitionDuration, transitionDelay } = window.getComputedStyle(element);
  const duration = Number.parseFloat(transitionDuration);
  const delay = Number.parseFloat(transitionDelay);

  return ((duration + delay) * MILLISECONDS_MULTIPLIER) + DURATION_PADDING;
};

/**
 * Executes a callback right after the element
 * has finished the transition.
 *
 * @param callback
 * @param element
 */
const executeAfterTransition = (
  callback: () => void,
  element: Element,
) => {
  const duration = getTransitionDuration(element);
  const TRANSITION_END = 'transitionend';
  let called = false;

  const handler = (event: Event) => {
    if (event.target !== element) return;

    called = true;
    element.removeEventListener(TRANSITION_END, handler);
    callback();
  };

  element.addEventListener(TRANSITION_END, handler);
  setTimeout(() => {
    if (!called) {
      element.dispatchEvent(new Event(TRANSITION_END));
    }
  }, duration);
};

/**
 * This callback is fired when the toast
 * is added to the DOM to show opening animation.
 *
 * @param element
 */
export const openAnimation = (element: HTMLDivElement | null) => {
  if (!element) return;

  nextTick(() => element.classList.add('active'));
};

/**
 * This callback is fired before the toast
 * is removed from the DOM to show closing animation.
 *
 * @param element
 * @param callback
 */
export const closeAnimation = (element: HTMLDivElement | null, callback: () => void) => {
  if (!element) return;

  element.classList.remove('active');
  executeAfterTransition(callback, element);
};

/**
 * Creates a random string of length 11 characters
 */
export const createRandomString = (): string => {
  return Math.random().toString(36).substring(2);
};

/**
 * Creates a new toast and adds it to the queue.
 *
 * @param type
 * @param text
 */
export const createToast = (type: ToastType, text: string) => {
  const id = createRandomString();
  const toast: ToastItem = {
    id,
    type,
    text,
  };
  dispatch({ type: 'ADD_TOAST', toast });
  return id;
};
